<template>
  <k-panel :title="panelTitle" :showContent="show" @toggle="(val) => show = val">
    <template #body>
      <slot name="preContent"></slot>
      <k-table
        class="learner-progress-table"
        :loading="loading"
        :rows="kableRows"
        :headers="kableHeaders"
        :defaultHeaders="defaultHeaders"
        :max="20"
        :hideable="true"
        :panel="false"
        :downloadable="true"
        :filename="panelTitle"
        :col-selector-options="colSelectorOptions"
        :fixed-column="1"
        :enableTopScroll="true"
      >
      </k-table>
    </template>
  </k-panel>
</template>

<style>
.learner-progress-table {
  margin: 15px;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import PageReadyMixin from '@mixins/page-ready-mixin';
import PluralMixin from '@mixins/plural-mixin';
import { formatId } from '@utils/format-user-id';
import {
  getOverallRagTableValue,
  getOofMetricValue,
  getRagStatus,
} from '@utils/rag-metrics';

export default {
  name: 'EmployerLearnerTable',

  components: {
    KPanel,
    KTable,

  },

  mixins: [PageReadyMixin, PluralMixin],

  props: {
    panelTitle: {
      type: String,
      required: true,
    },
    statusType: {
      type: Array,
      required: true,
    },
    learnerData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      show: true,
    };
  },

  computed: {
    rag_boundary_tooltip_text() {
      return `
        <ul style="margin:4px 0 0 4px;padding:0;list-style:none;">
          <li>Red: &lt; 60%</li>
          <li>Amber: 60-80%</li>
          <li>Green: 80%+</li>
        </ul>`;
    },
    ilrHeaders() {
      return {
        ilr_grading_outcome: {
          name: 'Grading Outcome',
          sortable: true,
          type: 'string',
          filterable: true,
        },
        ilr_completion_status: {
          name: 'ILR Status',
          sortable: true,
          type: 'string',
          hasDropdown: true,
          options: {
            color: true,
            tooltip: true,
            tooltipText: 'Current learner status according to the ILR',
          },
        },
        ilr_outcome: {
          name: 'ILR Outcome',
          sortable: true,
          type: 'string',
          filterable: false,
          options: {
            color: true,
            tooltip: true,
            tooltipText: 'Learner outcome according to the ILR',
          },
        },
        ilr_start_date: {
          name: 'Programme Start Date',
          sortable: true,
          type: 'timestamp',
          filterable: false,
          options: {
            color: true,
            tooltip: true,
            tooltipText: 'Start date for the programmes',
          },
        },
        ilr_end_date: {
          name: 'Planned End Date',
          sortable: true,
          type: 'timestamp',
          filterable: false,
          options: {
            color: true,
            tooltip: true,
            tooltipText: 'This is the date the learner should enter gateway. This signals the end of the funded period for a learner and does not include the EPA period that follows',
          },
        },
        ilr_actual_end_date: {
          name: 'Actual End Date',
          sortable: true,
          type: 'timestamp',
          filterable: false,
          options: {
            color: true,
            tooltip: true,
            tooltipText: this.getActualEndDateTooltip(),
          },
        },
      };
    },
    achievementHeaders() {
      return {
        ilr_achievement_date: {
          name: 'Achievement Date',
          sortable: true,
          type: 'timestamp',
          filterable: false,
        },
      };
    },
    otjHeaders() {
      return {
        expected_total_otj_hours: {
          name: 'OTJ Hours Expected (Total)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The learner\'s total required OTJ hours',
          },
        },
        expected_otj_hours_per_day: {
          name: 'OTJ Hours Expected (Per Day)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The learner\'s required OTJ hours per day',
          },
        },
        expected_otj_hours_to_month: {
          name: 'OTJ Hours Expected (As of Last Month)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The learner\'s required OTJ hours in their programme up to the end of the previous calendar month',
          },
        },
        approved_hours: {
          name: 'OTJ Hours Approved (Total)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The total number of OTJ hours approved by the learner\'s coach',
          },
        },
        approved_hours_until_this_month: {
          name: 'OTJ Hours Approved (As of Last Month)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The total number of OTJ hours approved by the learner\'s coach up to the end of the previous calendar month',
          },
        },
        approved_hours_this_month: {
          name: 'OTJ Hours Approved (Current Month)',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'The total number of OTJ hours approved out of the monthly expected hours this calendar month',
          },
        },
        latest_submission: {
          name: 'Last OTJ Submission',
          sortable: true,
          type: 'datetime',
          options: {
            tooltip: true,
            tooltipText: 'The submission date of the learner\'s most recent OTJ entry',
          },
        },
        latest_activity_date: {
          name: 'Last OTJ Activity',
          sortable: true,
          type: 'datetime',
          options: {
            tooltip: true,
            tooltipText: 'The activity date on the learner\'s last submitted OTJ entry',
          },
        },
      };
    },

    curriculumProgressHeaders() {
      return {
        programme_member_id: {
          name: 'Programme Member ID',
          sortable: true,
          type: 'number',
        },
        active_curriculum_progress: {
          name: 'Active Curriculum Progress',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% completion of KATE curriculum modules with a due date in the future',
          },
        },
        due_curriculum_progress: {
          name: 'Due Curriculum Progress',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% completion of KATE curriculum modules with a due date in the past',
          },
        },
        total_workshops: {
          name: 'Total Workshops',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Total workshops in curriculum',
          },
        },
        workshops_completed: {
          name: 'Workshops Completed',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Workshops marked as complete by the learner',
          },
        },
        workshops_completed_percentage: {
          name: '% Completed Workshops',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% workshops marked as complete by the learner',
          },
        },
        completed_live_workshops: {
          name: 'Completed Live Workshops',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Live workshops completed by the learner',
          },
        },
        live_workshops_completed_percentage: {
          name: '% Completed Live Workshops',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% of workshops completed by the learner that were live',
          },
        },
        completed_recorded_workshops: {
          name: 'Completed Recorded Workshops',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Recorded workshops completed by the learner',
          },
        },
        recorded_workshops_completed_percentage: {
          name: '% Completed Recorded Workshops',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% of workshops completed by the learner that were recorded',
          },
        },
        paks_completed: {
          name: 'Assignments Completed',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Due assignments completed by the learner',
          },
        },
        paks_completed_percentage: {
          name: '% Completed Assignments',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: '% Due assignments completed by the learner',
          },
        },
        total_paks: {
          name: 'Total Assignments',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Total due assignments available to the learner',
          },
        },
        last_completed_asset: {
          name: 'Last EDUKATE Completion',
          sortable: true,
          type: 'datetime',
          options: {
            tooltip: true,
            tooltipText: 'Date the learner last completed an asset',
          },
        },
        total_active_assets: {
          name: 'Total Active Assets',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Total assets with a due date in the future',
          },
        },
        total_assets_due: {
          name: 'Total Assets Due',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Total assets with a due date in the past',
          },
        },
        last_scheduled_meeting: {
          name: 'Last Scheduled Meeting',
          sortable: true,
          type: 'datetime',
          options: {
            tooltip: true,
            tooltipText: 'Most recent scheduled meeting that has passed',
          },
        },
        last_completed_meeting: {
          name: 'Last Completed Meeting',
          sortable: true,
          type: 'datetime',
          options: {
            tooltip: true,
            tooltipText: 'Most recent meeting that has been signed as completed',
          },
        },
        meetings_completed_percentage: {
          name: 'Planned Meetings Progress',
          sortable: true,
          type: 'percentage',
          options: {
            tooltip: true,
            tooltipText: 'Percentage of required training plan meetings with Cambridge Spark completed to date',
          },
        },
        total_meetings: {
          name: 'Total Meetings',
          sortable: true,
          type: 'number',
          options: {
            tooltip: true,
            tooltipText: 'Total meetings in the Meetings Milestone',
          },
        },
      };
    },

    ragStatusHeaders() {
      return {
        rag_applicable: {
          name: 'RAG Applicable',
          sortable: true,
          type: 'string',
          hasDropdown: true,
          displayAs: value => (value ? 'Yes' : 'No'),
          downloadAs: value => (value ? 'Yes' : 'No'),
          options: {
            tooltip: true,
            tooltipText: 'Whether the learner has RAG Statuses available',
          },
        },
        overall_rag: {
          name: 'Overall RAG',
          sortable: true,
          type: 'pill',
          sortBy: status => {
            const sortOrder = {
              'First 42 days': 0,
              red: 1,
              amber: 2,
              green: 3,
              'Green - In Assessment': 4,
            };
            return sortOrder[status];
          },
          options: {
            tooltip: true,
            tooltipText: 'If the learner is in EPA, their overall RAG Status will be Green',
            type: status => {
              const statusMap = {
                green: 'success',
                'Green - In Assessment': 'success',
                'First 42 days': 'neutral',
                amber: 'warning',
                red: 'danger',
              };
              return statusMap[status];
            },
            slotContent: status => {
              if (['First 42 days', 'Green - In Assessment'].includes(status)) {
                return status;
              }
              return null;
            },
          },
        },
        expected_otj_hours_to_month_rag: {
          name: 'OTJ On-Track',
          sortable: true,
          type: 'rag-dot',
          options: {
            tooltip: true,
            tooltipText: `The learner's approved OTJ up to last month, as percentage of expected up to last month.${this.rag_boundary_tooltip_text}`,
            metricType: 'percentage',
            metricField: 'perc_otj_hours',
            ragField: 'otj_rag',
          },
        },
        workshops_completed_percentage_rag: {
          name: 'Workshop Attendance',
          sortable: true,
          type: 'rag-dot',
          options: {
            tooltip: true,
            tooltipText: `% workshops marked as complete by the learner.${this.rag_boundary_tooltip_text}`,
            metricType: 'percentage',
            metricField: 'perc_workshop_attendance',
            ragField: 'workshop_rag',
          },
        },
        on_a_break: {
          name: 'On a Break',
          sortable: true,
          type: 'rag-dot',
          sortBy: (x, row) => this.getBilSortBy(row),
          options: {
            tooltip: true,
            tooltipText: 'Whether the learner is on a break in learning and the number of days past or until expected return date',
            metricType: 'string',
            ragField: 'bil_rag',
          },
        },
        funding_status: {
          name: 'Funding Status',
          sortable: true,
          type: 'rag-dot',
          sortBy: (x, row) => this.getOofSortBy(row),
          options: {
            tooltip: true,
            tooltipText: 'The learner\'s funding status and the number of days past the planned end date if the learner is out of funding',
            metricType: 'string',
            ragField: 'oof_rag',
          },
        },
      };
    },
    kableHeaders() {
      let headers = {
        learner: {
          name: 'User ID',
          sortable: true,
        },
        name: {
          name: 'Name',
          type: 'url',
          filterable: true,
          sortable: true,
          hideable: false,
          downloadAs: value => {
            // Extract the text from the object and clean any HTML
            if (value && typeof value === 'object' && value.text) {
              return value.text.replace(/<[^>]*>/g, '').trim();
            }
            return value;
          },
          options: {
            color: false,
            tooltip: true,
            tooltipText: 'A tooltip next to learner means they dropped out or completed another programme',
          },
        },
        uln: {
          name: 'ULN',
          sortable: true,
          type: 'string',
        },
        pics_email: {
          name: 'Email',
          sortable: true,
          type: 'string',
        },
        programme: {
          name: 'Programmes',
          filterable: false,
          hasMultiSelectDropdown: true,
          sortable: false,
        },
        academic_year: {
          name: 'Academic Year',
          sortable: true,
          type: 'string',
          filterable: true,
        },
        ...this.ilrHeaders,
      };
      if (this.statusType.includes('Completed Learning')) {
        headers = this.mergeHeaders(headers, this.achievementHeaders);
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-otj-metrics')) {
        headers = this.mergeHeaders(headers, this.otjHeaders);
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-curriculum-metrics')) {
        headers = this.mergeHeaders(headers, this.curriculumProgressHeaders);
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-rag-metrics')) {
        headers = this.mergeHeaders(headers, this.ragStatusHeaders);
      }
      return headers;
    },

    defaultHeaders() {
      const headers = this.$featureFlags.isEnabled('employer-dashboard-rag-metrics')
        ? [...this.ragStatus]
        : [...this.programmeContext];

      if (this.statusType.includes('Completed Learning') && !headers.includes('ilr_achievement_date')) {
        headers.push('ilr_achievement_date');
      }

      return headers;
    },

    programmeContext() {
      return [
        'name',
        'programme',
        'academic_year',
        'ilr_start_date',
        'ilr_end_date',
        'ilr_actual_end_date',
      ];
    },

    ragStatus() {
      return [
        'name',
        'programme',
        'overall_rag',
        'expected_otj_hours_to_month_rag',
        'workshops_completed_percentage_rag',
        'on_a_break',
        'funding_status',
      ];
    },

    ilrStatus() {
      return [
        'name',
        'programme',
        'academic_year',
        'ilr_completion_status',
        'ilr_outcome',
      ];
    },

    curriculumProgress() {
      return [
        'name',
        'programme',
        'due_curriculum_progress',
        'active_curriculum_progress',
        'completed_live_workshops',
        'live_workshops_completed_percentage',
        'total_live_workshops',
        'completed_recorded_workshops',
        'recorded_workshops_completed_percentage',
        'total_recorded_workshops',
        'workshops_completed',
        'workshops_completed_percentage',
        'total_workshops',
        'meetings_completed_percentage',
        'total_meetings',
        'paks_completed',
        'paks_completed_percentage',
        'total_paks',
      ];
    },

    engagement() {
      return [
        'name',
        'programme',
        'last_completed_asset',
        'last_scheduled_meeting',
        'last_completed_meeting',
        'workshops_completed_percentage',
        'meetings_completed_percentage',
        'latest_submission',
        'latest_activity_date',
      ];
    },

    otjHours() {
      return [
        'name',
        'programme',
        'expected_total_otj_hours',
        'expected_otj_hours_to_month',
        'approved_hours',
      ];
    },

    contacts() {
      return [
        'name',
        'programme',
        'line_manager_email',
        'coach_email',
        'data_mentor_email',
      ];
    },

    achievements() {
      return [
        'name',
        'programme',
        'ilr_grading_outcome',
        'ilr_outcome',
        'ilr_achievement_date',
      ];
    },

    colSelectorOptions() {
      const options = [
        {
          text: 'Programme Context',
          func: (col, key) => this.programmeContext.includes(key) && this.isColumnAvailable(key),
        },
        {
          text: 'ILR Status',
          func: (col, key) => this.ilrStatus.includes(key) && this.isColumnAvailable(key),
        },
      ];

      // TODO: add Contacts groupings

      if (this.statusType.includes('Completed Learning')) {
        options.push({
          text: 'Achievements',
          func: (col, key) => this.achievements.includes(key) && this.isColumnAvailable(key),
        });
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-otj-metrics') || this.$featureFlags.isEnabled('employer-dashboard-curriculum-metrics')) {
        options.push({
          text: 'Engagement',
          func: (col, key) => this.engagement.includes(key) && this.isColumnAvailable(key),
        });
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-otj-metrics')) {
        options.push({
          text: 'OTJ Hours',
          func: (col, key) => this.otjHours.includes(key) && this.isColumnAvailable(key),
        });
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-curriculum-metrics')) {
        options.push(
          {
            text: 'Curriculum Progress',
            func: (col, key) => this.curriculumProgress.includes(key) && this.isColumnAvailable(key),
          },
        );
      }
      if (this.$featureFlags.isEnabled('employer-dashboard-rag-metrics')) {
        options.push({
          text: 'RAG Status',
          func: (col, key) => this.ragStatus.includes(key) && this.isColumnAvailable(key),
        });
      }
      return options;
    },

    getFilteredLearners() {
      return this.learnerData.filter(val => {
        if (this.statusType.includes('Withdrawn') && val.withdrawn_in_first_42_days) {
          return false;
        }
        return this.statusType.includes(val.ilr_completion_status);
      });
    },

    kableRows() {
      return this.getFilteredLearners.map(val => ({
        learner: formatId(val.user_id),
        name: {
          text: val.user_name,
          path: {
            name: 'dashboard_students',
            params: { studentId: val.user_id },
          },
        },
        ...val,
        uln: val.uln,
        pics_email: val.pics_email,
        on_a_break: this.getBilMetricValue(val),
        funding_status: getOofMetricValue(val),
        overall_rag: getOverallRagTableValue(val),
        otj_rag: getRagStatus(val, 'otj_rag'),
        workshop_rag: getRagStatus(val, 'workshop_rag'),
        bil_rag: getRagStatus(val, 'bil_rag'),
        oof_rag: getRagStatus(val, 'oof_rag'),
      }));
    },
  },
  methods: {
    getActualEndDateTooltip() {
      // panelTitle: Completed Learners and statusType: Completed Learning;
      // panelTitle: Withdrawn Learners and statusType: Withdrawn
      // panelTitle: Active Learners and statusType: Continuing Learning or On a Break (default)
      if (this.statusType.includes('Completed Learning')) {
        return 'The actual date the learner entered gateway';
      } if (this.statusType.includes('Withdrawn')) {
        return 'The last day of evidenced learning';
      }
      return 'The actual date the learner entered gateway or the last day of evidenced learning for those on a BiL';
    },

    isColumnAvailable(key) {
      if (Object.keys(this.otjHeaders).includes(key)) {
        return this.$featureFlags.isEnabled('employer-dashboard-otj-metrics');
      }
      if (Object.keys(this.curriculumProgressHeaders).includes(key)) {
        return this.$featureFlags.isEnabled('employer-dashboard-curriculum-metrics');
      }
      if (Object.keys(this.ragStatusHeaders).includes(key)) {
        return this.$featureFlags.isEnabled('employer-dashboard-rag-metrics');
      }
      return true;
    },

    mergeHeaders(target, source) {
      const result = { ...target };
      Object.keys(source).forEach(key => delete result[key]);
      return { ...result, ...source };
    },

    getBilMetricValue(val) {
      if (val.ilr_completion_status !== 'On a Break') {
        return 'Not on a break';
      }
      if (val.days_post_exp_return_date == null) {
        return 'No return date';
      }
      if (val.days_post_exp_return_date === 0) {
        return 'Returning today';
      }
      return val.days_post_exp_return_date > 0 ? `${val.days_post_exp_return_date} days past return date` : `Returning in ${Math.abs(val.days_post_exp_return_date)} days`;
    },

    getBilSortBy(row) {
      if (row.ilr_completion_status !== 'On a Break') {
        return -Infinity;
      }
      if (row.days_post_exp_return_date == null) {
        return null;
      }
      return row.days_post_exp_return_date;
    },

    getOofSortBy(row) {
      if (row.days_post_planned_end_date == null) {
        return null;
      }
      return row.days_post_planned_end_date;
    },
  },
};
</script>
